import { useMemo, type PropsWithChildren } from "react";

import {
  type AirHandlerUnit,
  type Alarm,
  type BoilerRoom,
  type Building,
  type BuildingGraph,
  type BuildingGraphNode,
  type Floor,
  type Gateway,
  type HeatingNetwork,
  type Mesh,
  type Meter,
  type Module,
  type Multisensor,
  type Room,
  type UserRole,
  type Valve,
} from "@eisox/api/core-api";
import type { Dayjs } from "@eisox/dayjs";
import { indexGraph, type GraphIndex } from "@eisox/tools";
import { createContext } from "@radix-ui/react-context";

import { gatewaysToEnhancedGateways } from "~/helpers/gateway.helper";
import { multisensorsToEnhancedMultisensors } from "~/helpers/multisensor.helper";
import { getRoomsGroups, roomsToEnhancedRooms } from "~/helpers/room.helper";
import { valvesToEnhancedValves } from "~/helpers/valve.helper";
import type { Permission } from "~/types/auth.type";
import type { EnhancedGateway } from "~/types/gateway.type";
import type { EnhancedMultisensor } from "~/types/multisensor.type";
import type { EnhancedRoom, RoomGroup } from "~/types/room.type";
import type { EnhancedValve } from "~/types/valve.type";

interface BuildingProviderProps extends PropsWithChildren {
  role: UserRole;
  permissions: Permission;
  canSeeInstallerWarnings: boolean;
  building: Building;
  graph: BuildingGraph;
  floors: Floor[];
  rooms: Room[];
  gateways: Gateway[];
  meshes: Mesh[];
  multisensors: Multisensor[];
  valves: Valve[];
  boilerRooms: BoilerRoom[];
  airHandlerUnits: AirHandlerUnit[];
  heatingNetworks: HeatingNetwork[];
  modules: Module[];
  alarms: Alarm[];
  meters: Meter[];
  date: Dayjs;
}

interface BuildingContextValue {
  role: UserRole;
  permissions: Permission;
  canSeeInstallerWarnings: boolean;
  building: Building;
  graph: GraphIndex<BuildingGraphNode>;
  floors: Floor[];
  rooms: EnhancedRoom[];
  roomsGroups: RoomGroup[];
  gateways: EnhancedGateway[];
  valves: EnhancedValve[];
  multisensors: EnhancedMultisensor[];
  boilerRooms: BoilerRoom[];
  airHandlerUnits: AirHandlerUnit[];
  heatingNetworks: HeatingNetwork[];
  meshes: Mesh[];
  modules: Module[];
  alarms: Alarm[];
  meters: Meter[];
  date: Dayjs;
}

const [BuildingContextProvider, useBuildingContext] = createContext<BuildingContextValue>("BuildingProvider");

const BuildingProvider: React.FC<BuildingProviderProps> = ({
  role,
  permissions,
  canSeeInstallerWarnings,
  building,
  graph,
  floors,
  rooms,
  gateways,
  meshes,
  valves,
  multisensors,
  boilerRooms,
  airHandlerUnits,
  heatingNetworks,
  modules,
  alarms,
  meters,
  date,
  children,
}) => {
  const enhancedGateways = useMemo(
    () => gatewaysToEnhancedGateways(gateways, floors, meshes, date, permissions, canSeeInstallerWarnings),
    [gateways, floors, meshes, date, permissions, canSeeInstallerWarnings],
  );
  const enhancedValves = useMemo(
    () => valvesToEnhancedValves(valves, floors, gateways, rooms, date, role, permissions, canSeeInstallerWarnings),
    [valves, floors, gateways, rooms, date, role, permissions, canSeeInstallerWarnings],
  );
  const enhancedMultisensors = useMemo(
    () => multisensorsToEnhancedMultisensors(multisensors, floors, gateways, rooms, date),
    [multisensors, floors, gateways, rooms, date],
  );
  const enhancedRooms = useMemo(
    () =>
      roomsToEnhancedRooms(
        rooms,
        floors,
        enhancedValves,
        enhancedMultisensors,
        building,
        permissions,
        date.toISOString(),
      ),
    [rooms, floors, enhancedValves, enhancedMultisensors, building, permissions, date],
  );
  const roomsGroups = useMemo(() => getRoomsGroups(enhancedRooms), [enhancedRooms]);
  const graphIndex = useMemo(() => indexGraph(graph), [graph]);

  return (
    <BuildingContextProvider
      role={role}
      permissions={permissions}
      canSeeInstallerWarnings={canSeeInstallerWarnings}
      building={building}
      graph={graphIndex}
      floors={floors}
      rooms={enhancedRooms}
      roomsGroups={roomsGroups}
      gateways={enhancedGateways}
      valves={enhancedValves}
      multisensors={enhancedMultisensors}
      boilerRooms={boilerRooms}
      airHandlerUnits={airHandlerUnits}
      heatingNetworks={heatingNetworks}
      meshes={meshes}
      modules={modules}
      alarms={alarms}
      meters={meters}
      date={date}
    >
      {children}
    </BuildingContextProvider>
  );
};

export { BuildingProvider, useBuildingContext };
